// The shell's AI controls (src/aithora/ai-controls-routes.ts; Console V4, Spec V4 section 16):
// Pause AI and its status, the one approval queue ("Needs your OK") and the Activity log.
// Role and building come from the session on the server; nothing here picks them.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export interface AiPerson { id: string | null; name: string }

export interface AiStatus {
  real: boolean;
  /** Whether this role sees the AI chip at all. */
  show: boolean;
  viewer: { team: boolean; building: boolean; owner: boolean; userId: string };
  buildingName: string | null;
  /** The Aithora-wide stop (every building). */
  tenant:
    | { paused: false }
    | { paused: true; killSwitchId: string; at: string; reason: string | null; via: string; by: AiPerson; ledgerRef: string | null; refusedWhilePaused: number };
  /** The viewer's own building pause (building roles only). */
  building: { paused: boolean; at: string | null; reason: string | null; by: AiPerson | null; refusedWhilePaused: number } | null;
  /** The Aithora team: every building that has paused AI actions. */
  buildingsPaused?: { id: string; name: string; at: string | null; reason: string | null; by: AiPerson | null }[];
  can: { pause: boolean; releaseTenant: boolean; releaseBuilding: boolean };
  /** How many items wait in this viewer's queue. */
  pending: number;
}

export type ApprovalTier = 'low' | 'check' | 'care';
/** How hard an action is to take back (Console V5 approval cards): undoable, hard to undo, permanent. */
export type ApprovalReversibility = 'undo' | 'hard' | 'perm';

/** One field Edit may change on a queued action, with what will run now. */
export interface ApprovalEditField {
  key: string;
  label: string;
  kind: 'text' | 'longtext' | 'choice' | 'integer' | 'datetime' | 'emails' | 'emailList';
  value: string;
  options?: string[];
  max?: number;
}

export interface ApprovalItem {
  id: string;
  tier: ApprovalTier;
  label: string;
  originalLabel: string | null;
  edited: boolean;
  proposer: AiPerson;
  building: AiPerson;
  changes: string;
  tells: string;
  undo: string;
  createdAt: string;
  mine: boolean;
  selfBlocked: boolean;
  needsPasskey: boolean;
  /** The fields Edit may change (an edit changes what runs). Empty: this action cannot be edited. */
  editable: ApprovalEditField[];
  /** Who last edited it, when, and which fields; null until someone edits it. */
  editedBy: AiPerson | null;
  editedAt: string | null;
  editedFields: string[];
  // ── Console V5 (handoff "Approval cards"). Optional: an older server leaves them out, and a
  // card draws each row only when it is sent; it never makes one up.
  /** When it expires (approval-expiry.ts; the server owns the real expiry). */
  expiresAt?: string | null;
  /** What happens then, in words ("Expires in 2 hours, then goes to the nurse on duty."). */
  expiresNote?: string;
  /** Spend over the owner's limit: it waits for the owner's OK (spend-ok.ts). */
  ownerOk?: boolean;
  /** What set it off, in a line ("Because"). Not sent by the server yet. */
  why?: string | null;
  /** How hard it is to take back. Not sent by the server yet. */
  reversibility?: ApprovalReversibility | null;
  /** A detail came from an email, a web page or a file: it always needs a fresh OK and no rule can cover it. */
  untrusted?: boolean;
  /** The rule "Always allow this" would save (POST /assistant/rules {tool, mode: 'allow', conditions}). */
  rule?: { tool: string; sentence: string; conditions?: Record<string, unknown>; track?: string | null } | null;
}

/** An approved email still in its 60 seconds (the SENDING strip, with Undo). */
export interface ApprovalSending { proposalId: string; label: string; sendsAt: string }

export interface ApprovalDecisionRow {
  proposalId: string;
  state: 'APPROVED' | 'DECLINED';
  label: string;
  reason: string | null;
  by: AiPerson;
  at: string;
  passkey: boolean;
  held: boolean;
}

export interface ApprovalsResponse {
  real: boolean;
  /** False for a role with no queue (the panel shows no queue and no Activity tab). */
  can: boolean;
  /** The AI is paused: approvals record and run when it is back on. */
  paused: boolean;
  items: ApprovalItem[];
  /** V5: approved emails still in their 60 seconds, the viewer's own. */
  sending?: ApprovalSending[];
  recent: ApprovalDecisionRow[];
  /** The rubber-stamp guard: approvals in a row with no edit or decline, and the median minutes to decide. */
  habits: { streak: number; medianMinutes: number | null; decided: number };
}

/** V5 adds what a person's rules did: AUTO (ran under a rule), WAITING (its 60 s Undo), PRACTICE (would have done), UNDONE. */
export type AiOutcome = 'RAN' | 'PROPOSED' | 'APPROVED' | 'EDITED' | 'DECLINED' | 'REFUSED' | 'HELD' | 'PAUSED' | 'AUTO' | 'WAITING' | 'PRACTICE' | 'UNDONE';

export interface AiActivityRow {
  id: string;
  at: string;
  outcome: AiOutcome;
  text: string;
  why: string | null;
  by: AiPerson;
  building: AiPerson;
  /** view 'queue' opens the queue; 'status' opens AI status; anything else is a console view. */
  link: { view: string; recordId: string | null; label: string };
  ref: string;
  waitMinutes: number | null;
}

export interface AiActivityResponse {
  real: boolean;
  scope: 'every_building' | 'building';
  buildingName: string | null;
  rows: AiActivityRow[];
  nextCursor: string | null;
}

export interface ApprovalDecisionResult {
  real: boolean;
  /** BLOCKED: an edit made it fail the governance gate or a safety check, so it will not run. */
  state?: 'APPROVED' | 'DECLINED' | 'BLOCKED';
  edited?: boolean;
  label?: string;
  /** The gate's verdict on an edit (PASS or HOLD; a BLOCK comes back as state BLOCKED). */
  gate?: string;
  fields?: string[];
  held?: boolean;
  result?: string | null;
  reason?: string | null;
  message?: string;
}

/** GET /assistant/status: the AI chip, AI status and the paused bar. */
export const aiStatus = route<AiStatus>()('GET', `${A}/assistant/status`, { honest: true, readOnlySafe: true });
/** POST /assistant/pause: team stops Atlas everywhere; a building role pauses their building. */
export const aiPause = route<{ real: boolean; changed: boolean; scope: 'tenant' | 'building' }, void, { reason?: string }>()('POST', `${A}/assistant/pause`, { honest: true });
/** POST /assistant/resume: 'tenant' is the owner's alone; 'building' is the Facility Admin's or Aithora's. */
export const aiResume = route<{ real: boolean; changed: boolean; scope: 'tenant' | 'building' }, void, { stop: 'tenant' | 'building'; organizationId?: string }>()('POST', `${A}/assistant/resume`, { honest: true });
/** GET /assistant/approvals: Needs your OK, scoped by role and building. */
export const aiApprovals = route<ApprovalsResponse>()('GET', `${A}/assistant/approvals`, { honest: true, readOnlySafe: true });
/**
 * POST /assistant/approvals/:id: approve, edit or decline (with an optional reason). An edit
 * sends the changed fields (keys from the item's `editable`) and changes what runs.
 */
export const aiDecide = route<ApprovalDecisionResult, void, { decision: 'approve' | 'edit' | 'decline'; fields?: Record<string, string>; reason?: string; webauthnAssertion?: unknown }>()(
  'POST', `${A}/assistant/approvals/:id`, { honest: true, acceptStatuses: [202] });
/**
 * GET /assistant/approvals/:id: one request, for the text's tap link (/#approve=<id>). 410 when it
 * is no longer waiting (or not this viewer's: the two answer the same); canAct says whether this
 * person may decide it.
 */
export const aiApproval = route<{ real: boolean; item: ApprovalItem; canAct: boolean }>()('GET', `${A}/assistant/approvals/:id`, { honest: true, readOnlySafe: true });
/** POST /assistant/approvals/:id/undo: stop an approved email inside its 60 seconds (nothing is sent); 409 once it has gone. */
export const aiUndo = route<{ real: boolean; state: 'UNDONE'; sent: false; message: string }>()('POST', `${A}/assistant/approvals/:id/undo`, { honest: true });
/** POST /assistant/approvals/batch: approve several LOW items together. */
export const aiDecideBatch = route<{ real: boolean; approved: number; results: { id: string; status: number; state?: string; error?: string }[] }, void, { ids: string[] }>()(
  'POST', `${A}/assistant/approvals/batch`, { honest: true });
/** POST /assistant/passkey-options: the challenge a CARE approval confirms with. */
export const aiPasskeyOptions = route<{ real: boolean; options: Record<string, unknown> }>()('POST', `${A}/assistant/passkey-options`, { honest: true });
/** GET /ai/activity: what the AI did, proposed and was told, newest first. */
export const aiActivity = route<AiActivityResponse, { scope?: 'mine' | 'everyone'; outcome?: string; facility?: string; limit?: number }>()(
  'GET', `${A}/ai/activity`, { honest: true, readOnlySafe: true });
